import { showIfConditions, evaluateShowIfWith, conditionMatches } from '../form/conditionalFieldLabel';

const truthy = (value) => value === true || value === 1 || value === '1';

/**
 * recordValues — flatten every field's saved value across ALL groups into one
 * lookup, so a Show Condition can reference any field on the record rather than
 * only one in its own group (e.g. Rate Details keys off contractType, which
 * lives in its own group). Reference values arrive as { id, value }; the
 * readable `value` is what conditions compare against. A field's payloadKey is
 * registered too, so a condition may name either the form key or the DB key.
 */
export function recordValues(formGroups = []) {
  const values = {};
  for (const group of formGroups ?? []) {
    for (const field of group?.fields ?? []) {
      if (!field?.field || field.value === undefined) continue;
      const raw = field.value;
      const flat = (raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw)
        ? raw.value
        : raw;
      values[field.field] = flat;
      if (field.payloadKey && values[field.payloadKey] === undefined) {
        values[field.payloadKey] = flat;
      }
    }
  }
  return values;
}

/**
 * visibleInDetail — the detail view honours the same admin-configured Show
 * Condition the add/edit forms do, so a value that no longer applies to the
 * record is hidden instead of shown as stale (e.g. Candidate Rate on a C2C
 * candidate, where the rate belongs to the employer).
 *
 * FAIL OPEN: when any referenced field is absent from the record the condition
 * cannot be judged, so the content is shown. Hiding only ever happens on a
 * positive evaluation — an unresolvable condition must never silently swallow
 * data. `ignoreShowIfInDetail` is the admin escape hatch to always show it.
 */
export function visibleInDetail(config, values) {
  const conditions = showIfConditions(config?.showIf);
  if (!conditions.length || truthy(config?.ignoreShowIfInDetail)) return true;
  const evaluable = conditions.every(
    (c) => Object.prototype.hasOwnProperty.call(values ?? {}, c.field),
  );
  if (!evaluable) return true;
  return evaluateShowIfWith(config.showIf, (key) => values[key]);
}

/**
 * detailFieldLabel — the detail view honours the same admin-configured
 * Conditional Label (`labelWhen`) the add/edit forms do, so one field reads
 * correctly for every record: a submission's rate shows as "Candidate Rate" on
 * a W2 hire and "Employer Rate" on a C2C one, instead of the detail view
 * printing whichever label happened to be typed as the static default.
 *
 * Same rule and operators as the forms (conditionMatches), evaluated against
 * the record's own values instead of live form state.
 *
 * FAIL CLOSED, unlike visibleInDetail: when the referenced field is missing
 * from the record the condition cannot be judged, so the STATIC label is kept.
 * Showing the configured default is always safe; asserting the conditional
 * label on an unproven condition would mislabel real data.
 *
 * `ignoreLabelWhenInDetail` (per-field, set in Detail Groups) is the admin
 * escape hatch that pins the static label in the detail view while the forms
 * keep switching it.
 */
export function detailLabelRules(field) {
  // Rules authored in Detail Groups win outright over the form's own
  // Conditional Label: they are the more specific instruction, and an admin who
  // opens the detail editor to write one expects it to take effect. With none
  // authored, the field simply inherits the form rule, so a module that only
  // ever configures Form Groups behaves exactly as before.
  const own = field?.detailLabelWhen;
  if (Array.isArray(own) && own.length) return own;
  const inherited = field?.labelWhen;
  return inherited ? [inherited] : [];
}

export function detailFieldLabel(field, values) {
  const fallback = field?.label;
  if (truthy(field?.ignoreLabelWhenInDetail)) return fallback;

  for (const rule of detailLabelRules(field)) {
    if (!rule?.field || !rule?.label) continue;
    // FAIL CLOSED: an unjudgeable rule is skipped, not guessed at.
    if (!Object.prototype.hasOwnProperty.call(values ?? {}, rule.field)) continue;
    if (conditionMatches(rule, values[rule.field])) return rule.label;
  }
  return fallback;
}
